import { useEffect, useMemo, useRef, useState } from "react";

function fetchWithTimeout(url, timeoutMs) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeoutMs);
  const started = performance.now();
  return fetch(url, { method: "GET", cache: "no-store", signal: ctrl.signal })
    .then((res) => {
      const rtt = performance.now() - started;
      clearTimeout(id);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return rtt;
    })
    .catch((e) => {
      clearTimeout(id);
      throw e;
    });
}

/**
 * status: "good" | "slow" | "offline" | "unknown"
 */
export function useNetworkQuality(options = {}) {
  const {
    // Pode ser QUALQUER asset estático na pasta public (ex.: "/favicon.ico")
    assetUrl = "/favicon.ico",
    intervalMs = 10000,
    timeoutMs = 5000,
    hysteresis = 2,
    slowRttMs = 1500, // 800,
    slowDownlinkMbps = 1,
    slowApiRttMs = 1000, // 500,
  } = options;

  const [status, setStatus] = useState("unknown");
  const [lastRtt, setLastRtt] = useState(null);
  const counterRef = useRef(0);
  const targetRef = useRef("unknown");

  const connection = useMemo(() => (typeof navigator !== "undefined" ? navigator.connection : undefined), []);

  function estimateFromApi() {
    if (!connection) return null;
    const eff = connection.effectiveType;
    const downlink = connection.downlink;
    const rtt = connection.rtt;
    const saveData = connection.saveData;

    if (saveData) return "slow";
    if (eff && (eff === "slow-2g" || eff === "2g")) return "slow";
    if (typeof downlink === "number" && downlink < slowDownlinkMbps) return "slow";
    if (typeof rtt === "number" && rtt > slowApiRttMs) return "slow";
    return "good";
  }

  useEffect(() => {
    let mounted = true;

    async function checkOnce() {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setLastRtt(null);
        applyWithHysteresis("offline");
        return;
      }
      try {
        // cache-buster pra garantir que não vem do cache
        const url = `${assetUrl}${assetUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;
        const rtt = await fetchWithTimeout(url, timeoutMs);
        if (!mounted) return;
        setLastRtt(rtt);

        let next = rtt > slowRttMs ? "slow" : "good";
        const apiEst = estimateFromApi();
        if (apiEst === "slow") next = "slow";
        applyWithHysteresis(next);
      } catch {
        if (!mounted) return;
        setLastRtt(null);
        applyWithHysteresis("offline");
      }
    }

    function applyWithHysteresis(next) {
      if (targetRef.current !== next) {
        targetRef.current = next;
        counterRef.current = 1;
      } else {
        counterRef.current += 1;
      }
      if (counterRef.current >= hysteresis) setStatus(next);
    }

    checkOnce();

    function onConnChange() {
      checkOnce();
    }
    if (connection && connection.addEventListener) {
      connection.addEventListener("change", onConnChange);
    }

    const id = setInterval(() => {
      if (document.visibilityState === "visible") checkOnce();
    }, intervalMs);

    const onOnline = () => checkOnce();
    const onOffline = () => applyWithHysteresis("offline");
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      mounted = false;
      clearInterval(id);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      if (connection && connection.removeEventListener) {
        connection.removeEventListener("change", onConnChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetUrl, intervalMs, timeoutMs, hysteresis, slowRttMs, slowDownlinkMbps, slowApiRttMs, connection]);

  return { status, lastRtt };
}
